import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { dataFetch } from '../api';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [billingState, setBillingState] = useState(null);
  const [loading, setLoading] = useState(true);

  const blockBannedProfile = async (profile) => {
    if (!profile?.banned_at) return false;
    setUser(null);
    setBillingState(null);
    localStorage.removeItem('user_id');
    await supabase.auth.signOut().catch(() => {});
    if (typeof window !== 'undefined' && window.location.hostname.startsWith('app.')) {
      window.location.replace('/login?conta=bloqueada');
    }
    return true;
  };

  const applyBillingTier = async (profile) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      const billing = { active: false, tier: 'rotina', source: 'none', entitlements: [] };
      setBillingState(billing);
      return { ...profile, plan_tier: 'rotina', is_pro: false };
    }
    try {
      const response = await fetch('/.netlify/functions/billing-entitlement', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Falha ao validar plano.');
      const tier = ['inicial', 'rotina', 'pro'].includes(payload?.tier) ? payload.tier : 'rotina';
      const billing = { ...payload, tier };
      setBillingState(billing);
      return { ...profile, plan_tier: tier, is_pro: tier === 'pro' };
    } catch (err) {
      console.error('Billing entitlement fetch error:', err);
      setBillingState({ active: false, tier: 'rotina', source: 'error', entitlements: [] });
      return { ...profile, plan_tier: 'rotina', is_pro: false };
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await fetchUserProfile(session.user);
        } else {
          setUser(null);
          setBillingState(null);
        }
      } catch (err) {
        console.error('Initial session error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        await fetchUserProfile(session.user);
      } else {
        setUser(null);
        setBillingState(null);
      }
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (authUser) => {
    try {
      localStorage.setItem('user_id', authUser.id);

      const userRes = await dataFetch(`read/users?id=${authUser.id}`);

      if (userRes?.status === 'success' && userRes.data?.[0]) {
        const profile = userRes.data[0];
        if (await blockBannedProfile(profile)) return;
        setUser(await applyBillingTier(profile));
      } else {
        const isDocAdmin = authUser.email === 'doc@eidoc.com.br';
        const newUser = {
          id: authUser.id,
          email: authUser.email,
          name: authUser.user_metadata?.name || authUser.email.split('@')[0],
          plan_tier: 'rotina',
          trial_ends_at: isDocAdmin ? null : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          is_pro: false,
          role: isDocAdmin ? 'admin' : 'user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        await dataFetch('create/users', {
          method: 'POST',
          body: JSON.stringify(newUser)
        });
        setUser(await applyBillingTier(newUser));
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('user_id');
      setUser(null);
      setBillingState(null);
      window.location.href = '/login';
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const updateProfile = async (data) => {
    if (!user?.id) return;
    try {
      const {
        plan_tier: _ignoredPlanTier,
        is_pro: _ignoredIsPro,
        trial_ends_at: _ignoredTrialEndsAt,
        banned_at: _ignoredBannedAt,
        ban_reason: _ignoredBanReason,
        role: _ignoredRole,
        ...safeData
      } = data || {};
      const res = await dataFetch(`update/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...safeData, updated_at: new Date().toISOString() })
      });
      if (res?.status === 'success') {
        const updatedUser = await applyBillingTier({ ...user, ...safeData });
        setUser(updatedUser);
        return updatedUser;
      }
      throw new Error(res?.error || 'Erro ao atualizar perfil');
    } catch (err) {
      console.error('Update profile error:', err);
      throw err;
    }
  };

  const refreshProfile = async () => {
    if (!user?.id) return;
    const res = await dataFetch(`read/users?id=${user.id}`);
    if (res?.status === 'success' && res.data?.[0]) {
      if (await blockBannedProfile(res.data[0])) return;
      setUser(await applyBillingTier(res.data[0]));
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      billingState,
      loading,
      signOut,
      updateProfile,
      refreshProfile,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin' || user?.email === 'doc@eidoc.com.br'
    }}>
      {children}
    </AuthContext.Provider>
  );
};
